// Transport bar: play/pause/±10/speed/quality/view + inline clip-info.
//
// Replaces a separate inspector column and a separate clip bar. Clip-info
// (camera · timestamp · duration · badges · AVA-request popover · Export)
// floats to the right of the controls; the surface keeps width for the video.

import type { ClipTier } from "@/api/clipsClient";

export type ViewMode = "single" | "grid";

export interface ClipInfo {
  /** Title (camera name or "N cameras · synced"). */
  title: string;
  /** Subtitle (location · timestamp · duration / segments). */
  sub: string;
  /** Pre-rendered badge nodes (already classNamed). */
  badges: JSX.Element | null;
  /** AVA request body shown inside the popover. */
  requestBody: string;
  /** Export button label (varies by clip kind). */
  exportLabel: string;
  /** Whether export is allowed (capabilities + has-recording). */
  exportEnabled: boolean;
  /** Tooltip when export is disabled. */
  exportDisabledReason?: string;
  /** Click handler for export. */
  onExport(): void;
}

export interface TransportBarProps {
  /** True when the player is currently playing. */
  playing: boolean;
  /** True when there's something loaded (enables play / extend). */
  hasLoaded: boolean;
  view: ViewMode;
  speed: number;
  tier: ClipTier;
  hdDisabled: boolean;
  /** Clip-info panel; null hides the panel entirely (no clip yet). */
  clipInfo: ClipInfo | null;

  onTogglePlay(): void;
  onExtend(direction: -1 | 1): void;
  onChangeSpeed(s: number): void;
  onChangeTier(t: ClipTier): void;
  onChangeView(v: ViewMode): void;
}

const SPEEDS = [1, 2, 4, 8, 16];

export function TransportBar({
  playing,
  hasLoaded,
  view,
  speed,
  tier,
  hdDisabled,
  clipInfo,
  onTogglePlay,
  onExtend,
  onChangeSpeed,
  onChangeTier,
  onChangeView,
}: TransportBarProps): JSX.Element {
  return (
    <div className={`rec-transport${clipInfo === null ? " rec-no-clip" : ""}`}>
      <button
        type="button"
        className="rec-play"
        onClick={onTogglePlay}
        disabled={!hasLoaded}
        title="Play / pause"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? "❚❚" : "▶"}
      </button>
      <button
        type="button"
        className="rec-btn"
        onClick={() => onExtend(-1)}
        disabled={!hasLoaded}
        title="Extend the clip 10 minutes earlier"
      >
        ⟨ 10 min before
      </button>
      <button
        type="button"
        className="rec-btn"
        onClick={() => onExtend(1)}
        disabled={!hasLoaded}
        title="Extend the clip 10 minutes later"
      >
        10 min after ⟩
      </button>
      <div className="rec-seg-group" role="group" aria-label="Playback speed">
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            className={s === speed ? "on" : ""}
            onClick={() => onChangeSpeed(s)}
            aria-pressed={s === speed}
          >
            {s}×
          </button>
        ))}
      </div>
      <div className="rec-seg-group dark" role="group" aria-label="Render profile">
        <button
          type="button"
          className={tier === "scan" ? "on" : ""}
          onClick={() => onChangeTier("scan")}
          title="SD proxy — 360p, fast to load & scrub"
        >
          SD · 360p
        </button>
        <button
          type="button"
          className={tier === "hd" ? "on" : ""}
          onClick={() => onChangeTier("hd")}
          disabled={hdDisabled || view === "grid"}
          title={
            view === "grid"
              ? "HD · 1080p is single-camera only — switch to Single view for HD evidence."
              : hdDisabled
                ? "HD · 1080p unavailable for clips over 10 min — the 1080p transcode times out (504). Narrow the range to use HD."
                : "HD evidence — 1080p, for reading faces / plates"
          }
        >
          HD · 1080p
        </button>
      </div>
      <div className="rec-seg-group dark" role="group" aria-label="View mode">
        <button
          type="button"
          className={view === "single" ? "on" : ""}
          onClick={() => onChangeView("single")}
          title="Single camera"
        >
          ▭ Single
        </button>
        <button
          type="button"
          className={view === "grid" ? "on" : ""}
          onClick={() => onChangeView("grid")}
          title="Synced 2×2 grid (SD, up to 4)"
        >
          ▦ Grid
        </button>
      </div>
      {clipInfo !== null && (
        <>
          <div className="rec-clip-info rec-t-right">
            <span className="rec-ttl">{clipInfo.title}</span>
            <span className="rec-sub">{clipInfo.sub}</span>
            <span className="rec-badges">{clipInfo.badges}</span>
          </div>
          <div className="rec-clip-actions">
            <span className="rec-req-pop">
              <button type="button" className="rec-btn rec-btn-sm">
                AVA request ▾
              </button>
              <div className="rec-req">{clipInfo.requestBody}</div>
            </span>
            <button
              type="button"
              className="rec-btn rec-btn-sm"
              disabled={!clipInfo.exportEnabled}
              title={clipInfo.exportDisabledReason}
              onClick={clipInfo.onExport}
            >
              ⬇ {clipInfo.exportLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
